import { Box, Button, FormLabel, Group, IconCrossCircle, Input, Typography } from '@screentone/core';
import { cloneDeep } from 'lodash';

import { Metadata, PageModule } from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import { useIsAdmin, useIsSuper } from 'hooks/roles';
import { getModule, handleChangePageModule } from 'utils/modules';
import styles from '../PageModule.module.scss';

interface PageModuleTitleProps {
  pageModule: PageModule;
  onChange: (newPageModule: PageModule) => void;
  viewOnly?: boolean;
  initialPageModule: PageModule;
}

type FieldKey = 'title' | 'titleUrl' | 'subtitle' | 'subtitleUrl' | 'summaryPriority';

export const PageModuleTitle = ({ pageModule, onChange, viewOnly, initialPageModule }: PageModuleTitleProps) => {
  const { data: publicationSettingsResp } = usePublicationSettings();
  const publicationSettings = cloneDeep(publicationSettingsResp?.publicationSetting);
  const isAdmin = useIsAdmin();
  const isSuper = useIsSuper();

  // For WSJ Opinion, we need to remove the '/opinion' part from the base route
  if (publicationSettings?.publicationKey === 'wsjopinion') {
    publicationSettings.baseRoute = publicationSettings.baseRoute.replace('/opinion', '');
  }

  const module = getModule(pageModule);
  const initialModule = getModule(initialPageModule);

  if (!module) return null;

  // Narrowed to modules with a title field (UiBasicModule | UiRankedModule | UiTitleModule | UiTitlePinnedModule)
  const titleModule = 'title' in module ? module : null;
  const initialTitleModule = initialModule && 'title' in initialModule ? initialModule : null;
  const allowedMeta = (titleModule && 'allowedMeta' in titleModule ? titleModule.allowedMeta : null) ?? [
    Metadata.Title,
    Metadata.Subtitle,
    Metadata.SubtitleUrl
  ];
  const titleUrl = titleModule && 'titleUrl' in titleModule ? titleModule.titleUrl : null;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const hasBaseRoute = titleUrl?.startsWith('http://') || titleUrl?.startsWith('https://');

  const handleChangeStringField = (fieldKey: FieldKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPageModule = handleChangePageModule(fieldKey, pageModule, e.target.value);
    onChange(newPageModule);
  };

  const handleClearField = (fieldKey: FieldKey) => () => {
    const newPageModule = handleChangePageModule(fieldKey, pageModule, '');
    onChange(newPageModule);
  };

  return (
    <>
      {titleModule && (allowedMeta.includes(Metadata.Title) || allowedMeta.includes('title' as Metadata)) && (
        <FormLabel key="title" label="Title" fullWidth>
          <Group gap="xs" className={styles.inputContainer}>
            <Input
              placeholder="Enter title"
              value={titleModule.title}
              margin={{ bottom: 'sm' }}
              onChange={handleChangeStringField('title')}
              disabled={viewOnly}
              data-testid="page-enter-title"
            />
            {!viewOnly && (
              <Button tertiary icon={IconCrossCircle} className={styles.deleteIcon} onClick={handleClearField('title')} />
            )}
          </Group>
        </FormLabel>
      )}
      {titleModule &&
        (allowedMeta.includes(Metadata.TitleUrl) || allowedMeta.includes('titleUrl' as Metadata)) &&
        (initialTitleModule && 'titleUrl' in initialTitleModule ? initialTitleModule.titleUrl : null) !== null && (
          <FormLabel key="titleUrl" label="Title URL Path" fullWidth margin={{ top: 'sm' }}>
            <Group gap="xs" className={styles.inputContainer}>
              {!hasBaseRoute && (
                <Box className={styles.baseRouteBox} padding={{ left: 'xs', right: 'xs' }}>
                  <Typography>{publicationSettings?.baseRoute}/</Typography>
                </Box>
              )}
              <Input
                placeholder="Enter title url path"
                value={hasBaseRoute ? titleUrl : titleUrl?.slice(1)}
                margin={{ bottom: 'sm' }}
                onChange={handleChangeStringField('titleUrl')}
                disabled={viewOnly}
                data-testid="page-enter-title-url"
              />
              {!viewOnly && (
                <Button
                  tertiary
                  icon={IconCrossCircle}
                  className={styles.deleteIcon}
                  onClick={handleClearField('titleUrl')}
                />
              )}
            </Group>
          </FormLabel>
        )}
      {titleModule &&
        (allowedMeta.includes(Metadata.Subtitle) || allowedMeta.includes('subtitle' as Metadata)) &&
        'subtitle' in titleModule && (
          <FormLabel key="subtitle" label="Subtitle" fullWidth margin={{ top: 'sm' }}>
            <Group gap="xs" className={styles.inputContainer}>
              <Input
                placeholder="Enter subtitle"
                value={titleModule.subtitle}
                margin={{ bottom: 'sm' }}
                onChange={handleChangeStringField('subtitle')}
                disabled={viewOnly}
                data-testid="page-enter-subtitle"
              />
              {!viewOnly && (
                <Button
                  tertiary
                  icon={IconCrossCircle}
                  className={styles.deleteIcon}
                  onClick={handleClearField('subtitle')}
                />
              )}
            </Group>
          </FormLabel>
        )}
      {titleModule &&
        (allowedMeta.includes(Metadata.SubtitleUrl) || allowedMeta.includes('subtitleUrl' as Metadata)) &&
        'subtitleUrl' in titleModule && (
          <FormLabel key="subtitleUrl" label="Subtitle URL Path" fullWidth margin={{ top: 'sm' }}>
            <Group gap="xs" className={styles.inputContainer}>
              <Box className={styles.baseRouteBox} padding={{ left: 'xs', right: 'xs' }}>
                <Typography>{publicationSettings?.baseRoute}/</Typography>
              </Box>
              <Input
                placeholder="Enter subtitle url path"
                value={titleModule.subtitleUrl?.slice(1)}
                margin={{ bottom: 'sm' }}
                onChange={handleChangeStringField('subtitleUrl')}
                disabled={viewOnly}
                data-testid="page-enter-subtitle-url"
              />
              {!viewOnly && (
                <Button
                  tertiary
                  icon={IconCrossCircle}
                  className={styles.deleteIcon}
                  onClick={handleClearField('subtitleUrl')}
                />
              )}
            </Group>
          </FormLabel>
        )}
      {(isAdmin || isSuper) && 'summaryPriority' in module && (
        <FormLabel key="summaryPriority" label="Summary Priority" fullWidth margin={{ top: 'sm' }}>
          <Group gap="xs" className={styles.inputContainer}>
            <Input
              placeholder="Enter summary priority"
              value={module.summaryPriority ?? ''}
              margin={{ bottom: 'sm' }}
              onChange={handleChangeStringField('summaryPriority')}
              disabled={viewOnly}
              data-testid="page-enter-summary-priority"
            />
            {!viewOnly && (
              <Button
                tertiary
                icon={IconCrossCircle}
                className={styles.deleteIcon}
                onClick={handleClearField('summaryPriority')}
              />
            )}
          </Group>
        </FormLabel>
      )}
    </>
  );
};
